import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'zone-one');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || activeCategory },
  ];

  const schemaData = {
    name: currentCategory?.name || '',
    description: currentCategory?.description || '',
    url: `https://japan-zone-guide.readdy.ai/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://japan-zone-guide.readdy.ai/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={schemaData} />
      <div className="min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto pt-6 pb-14">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8 md:mb-10">
            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
              {currentCategory?.name || activeCategory}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {currentCategory?.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap" role="tablist">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  role="tab"
                  aria-selected={activeCategory === cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Article List */}
          {categoryArticles.length > 0 ? (
            <div className="space-y-4">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row gap-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
                >
                  <div className="relative w-full sm:w-48 h-36 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 py-4 pr-5 pl-5 sm:pl-0">
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 mb-1.5 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    <div className="flex gap-1.5 mt-3 flex-wrap">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </span>
              <p className="text-foreground-600">暂无相关文章</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}